import React, {Component, useContext, useEffect} from 'react';
import ReactDOM from 'react-dom';
import { Button, ButtonClose, ButtonDownload, ButtonCancel } from '../../components/buttons';
import { Layout, MinMaxLayout, SeventyThirtyLayout, SixtyFourtyLayout } from '../../layouts';
import Welcome from '../../layouts/welcome';
import Row from '../../components/row';
import Container from '../../layouts/containers/container';
import ActivityItemNew from '../../components/activity/activity-item-new';
import List from '../../components/list';
import InputsDropdown from '../../components/inputs/inputs-dropdown';
import SearchBar from '../../components/searchbar';
import ContainerTable from '../../layouts/containers/container-table';
import { stringToFriendlyDatetime, getDSTLocalTime } from '../../lib/helpers/datetime-helpers';
import ContainerIndex from '../../layouts/containers/container-index';
import ThemeContext from '../../context/context-theme';
import Grid from '../../components/grid';
import InputsCheckbox from '../../components/inputs/inputs-checkbox';
import LabelContainer from '../../components/label-container';
import Divider from '../../components/divider/divider';
import Blurred from '../../components/blurred';
import InputsCounter from '../../components/inputs/inputs-counter';
import DateTimeInput from '../../components/inputs/date-time-input';

import ButtonModify from '../../components/buttons/button-modify';
import meetingsApi from '../../lib/meetings/meetings-api';
import PopupContext from '../../context/context-popup';
import PopupsAdditionalPackageDetails from './popups/popups-additional-package-details';

import MediaHelper from '../../lib/helpers/media';
import companiesApi from '../../lib/companies/companies-api';

import StatusBar from './components/status-bar';
import PopupsAddGuest from './popups/popups-add-guest';
import PopupsCancelBooking from './popups/popups-cancel-booking';
import BillingDetails from '../../components/billing-profile';

const Package = ({ onAdditionalDetailsAdd, booking, addon = {}, addonPackage, data }) => {
    const { setPopup } = useContext(PopupContext);

    const showAdditionalRequirements = () => {
        setPopup(<PopupsAdditionalPackageDetails onSuccess={onAdditionalDetailsAdd} booking={booking} addon={addon} addonPackage={addonPackage}/>);
    }

    return (
        <div className="package">
            <div className="package__details">
                <div className="package-details__top">
                    <div style={{ marginRight: '20px' }} className="package__checkbox">{ data?.quantity }</div>
                    <p>{ data?.name }</p>
                </div>
                <List gap="20px">
                    <p className="package-details__description" dangerouslySetInnerHTML={{ __html: data?.package_description }}></p>

                    { addon?.show_additional_requirements_field && booking.canceled === null && <ButtonModify onClick={showAdditionalRequirements}>{ addon?.additional_requirements_label || 'Additional Requirements' }</ButtonModify> }
                </List>
            </div>
        </div>
    )
}

const Addons = ({ 
    addons = [],
    packages = [],
    booking,
    onAdditionalDetailsAdd
}) => {
    return (
        <Container>
            <Container.Header>
                <Row gap="20px">
                    <Container.Text color="#FF6933" fontSize="18px">Addons</Container.Text>
                </Row>
            </Container.Header>

            <Container.Content>
                <List gap="40px">
                    { addons?.map((addon, index) => {
                        const addonPackages = packages.filter((p) => addon?.add_on_packages?.find(pa => pa.id === p.add_on_package_id));

                        if (addonPackages.length === 0) return null;

                        return (
                            <Container key={index}>
                                <Container.Header>
                                    <List gap="20px">
                                        <Container.Text color="white" fontSize="20px">{ addon?.name }</Container.Text>
                                        <Container.Text color="#929BA8" fontSize="14px">Choose from one of our catering packages below to enhance your meeting experience for you and your guests. Dietary requirements will be collected after your booking is confirmed.</Container.Text>
                                    </List>
                                </Container.Header>
                    
                                <Container.Content>
                                    <List gap="40px">
                                        { addonPackages?.map((p, index) => {
                                            return <Package 
                                                key={index} 
                                                addon={addon}
                                                addonPackage={p}
                                                booking={booking}
                                                onAdditionalDetailsAdd={onAdditionalDetailsAdd}
                                                data={{
                                                    name: p?.add_on_package?.name,
                                                    quantity: p?.quantity,
                                                    package_description: p?.add_on_package?.package_description
                                                }}
                                            />
                                        } )}
                                    </List>
                                </Container.Content>
                            </Container>
                        )
                    }) }
                </List>
            </Container.Content>
        </Container>
    )
}


const YourDetails = ({ booker }) => {
    const Item = ({ title, value }) => {
        return (
            <List gap='10px' style={{ flex: '1 0 0' }}>
                <LabelContainer label={title}>
                    { value }
                </LabelContainer>
            </List>
        )
    }

    const Flex = ({ direction, children }) => {
        return (
            <div style={{
                display: 'flex',
                flexDirection: direction,
                gap: '20px',
            }}>
                { children }
            </div>
        )
    }

    const profile = booker?.billing_profile;

    return (
        <Container>
            <Container.Header>
                <Container.Text color="#FF6933" fontSize="18px">Your Billing Details</Container.Text>
            </Container.Header>

            <Container.Content>
                <Flex direction="column">
                    <Flex direction="row">
                        <Item title="First Name" value={profile?.first_name}/>
                        <Item title="Last Name" value={profile?.last_name}/>
                    </Flex>
                    <Flex direction="row">
                        <Item title="Company" value={profile?.company_name}/>
                    </Flex>
                    <Item title="Email Address" value={profile?.email_address}/>
                    <Flex direction="row">
                        <Item title="Telephone Number" value={profile?.tel_no}/>
                        <Item title="SAP Number" value={profile?.sap_number}/>
                    </Flex>
                </Flex>
            </Container.Content>
        </Container>
    )
}

const Summary = ({
    quote = {}
}) => {
    const getSavedQuoteLineItem = (relatedTo) => {
        const lineItems = quote?.saved_quote_line_items;

        const item  = lineItems.find(l => l.related_to = relatedTo);

        return item || {};
    }

    const data = quote?.meta?.add_on_packages?.map((addon) => {
        const price = quote?.meta?.addons_pricing?.find((a) => a.id === addon.add_on_package_id)?.price;

        return [
            addon?.quantity || 0,
            addon?.add_on_package?.name,
            `£${price || 0}`
        ]
    }) || [];

    const cost = quote?.meta?.meeting_room_price_less_credits;
    const credits = quote?.credits_used;

    let price = credits > 0 ? `${credits} credit(s)` : `£${cost}`;

    if (cost > 0 && credits > 0) {
        price = `£${cost} + ${credits} credit(s)`;
    }

    data.unshift([
        1,
        'Meeting Room Booking',
        price,
    ])

    return (
        <ContainerTable
            showMobileColumns
            headerColor="#d5d5d5"
            valueColor='white'
            className="table-white"
            columns={[
                { field: 'QTY', width: '25%' },
                { field: 'Item', width: '50%' },
                { field: 'Price/Credit(s)', width: '25%' },
            ]}
            data={[
                ...data
            ]}
        />
    )
}

const GuestListButton = ({ booking, onCreate }) => {
    const { setPopup } = useContext(PopupContext);

    const showGuestList = () => setPopup(<PopupsAddGuest booking={booking} onCreate={onCreate}/>);

    return <ButtonModify onClick={showGuestList}>Guest List</ButtonModify>
}

const TopRightBar = ({ booking, fetch }) => {
    const { setPopup } = useContext(PopupContext);

    const cancelBooking = () => {
        setPopup(<PopupsCancelBooking booking={booking} refresh={fetch}/>);
    }

    const goBack = () => window.history.back();

    return (
        <Row gap="20px" wrap>
            <ButtonClose responsive onClick={goBack}>Close</ButtonClose>
            <ButtonDownload responsive>Print</ButtonDownload>
            { booking.canceled === null && <ButtonModify responsive onClick={() => window.location.href = `/mydmc/bookings/${booking.id}/edit`}>Modify</ButtonModify> }
            { booking.canceled === null && <ButtonCancel responsive onClick={cancelBooking}>Cancel Booking</ButtonCancel> }
        </Row>
    )
}

class BookingsView extends Component {
    static contextType = PopupContext;

    constructor(props) {
        super(props);

        this.state = {
            booking: null,
            addons: [],
            data: null,
            room: null,
            quote: null,
        }
    }

    componentDidMount() {
        this.fetchBooking();
        this.fetchCompanyInformation();
        this.fetchAddons();
    }

    fetchBooking() {
        meetingsApi.getMeetingRoomBooking(this.props.id).then(({ data }) => {
            this.setState({ booking: data })

            this.fetchMeetingRoom();
        });
    }

    fetchMeetingRoom() {
        meetingsApi.getMeetingRoom(this.state.booking.meeting_room_id).then(({ data }) => {
            this.setState({ room: data });
        })
    }

    fetchCompanyInformation() {
        companiesApi.getCompany(window.user.company_id).then(({ data }) => {
            this.setState({ company: data });
        });
    }

    fetchAddons() {
        meetingsApi.getAddons().then(({ data }) => {
            this.setState({ addons: data });
        });
    }

    changeLocation(url) {
        window.location.href = url;
    }

    getFeaturedImage() {
        const image = this.state.room?.content?.room_feature_image?.[0];

        if (image?.file_name === undefined) return '/images/office.png';

        return MediaHelper.formatPublicPath(`/images/${image?.file_name}`);
    }

    getSavedQuoteLineItem(relatedTo)
    {
        const lineItems = this.state.booking?.saved_quote?.saved_quote_line_items;

        const item  = lineItems.find(l => l.related_to = relatedTo);

        return item || {};
    }

    getLocation() {
        const { name, address_one, address_two, address_post_code, address_city } = this.state.room?.office_building || {
            name: '',
            address_one: '',
            address_two: '',
            address_post_code: '',
            address_city: ''
        };

        let string = [
            name,
            address_one,
            address_two,
            address_post_code,
            address_city
        ].filter(e => e !== undefined && e !== null);

        return string.join(', ');
    }

    cancelBooking() {
        const { setPopup } = this.context;

        setPopup(<PopupsCancelBooking booking={this.state.booking} refresh={this.fetchBooking.bind(this)}/>);
    }

    render() {
        if (this.state.booking === null) return null;

        const featuredImage = this.getFeaturedImage();
        const location = this.getLocation();

        const quote = this.state.booking?.receipt || this.state.booking?.saved_quote;

        return (
            <Layout 
                theme="dark"
                topLeft={<Welcome title={<Container.Text>Thanks, your meeting room is booked</Container.Text>} subtitle={this.state.booking?.name || 'Meeting Room Booking'}/>}
                topRight={
                    <TopRightBar booking={this.state.booking} fetch={this.fetchBooking.bind(this)}/>
                }
            >
                <MinMaxLayout
                    main={
                        <List gap="40px">
                            { this.state.booking.canceled !== null && <StatusBar
                                status="error"
                                head="Booking Cancelled"
                                content={
                                    <p>
                                        This booking has been cancelled on <b>{ getDSTLocalTime(this.state.booking?.canceled).format('HH:mm D/M/YYYY') }</b> by <b>{this.state.booking?.canceled_by?.name}</b>
                                    </p>
                                }
                            /> }
                            <div className="booking-view">
                                <List gap="40px" divider={<Divider/>} className="booking-view__child">
                                    <Container>
                                        <Container.Header>
                                            <Container.Text color="#FF6933" fontSize="18px">Booking Details</Container.Text>
                                        </Container.Header>

                                        <Container.Content> 
                                            <List gap="20px">
                                                <div className="booking-view__image" style={{ backgroundImage: `url('${featuredImage}')` }}></div>

                                                <LabelContainer label='Booking Type'>
                                                    { this.state.booking?.saved_quote?.meta?.meeting_room?.name }
                                                </LabelContainer>

                                                <LabelContainer label='Booking Location'>
                                                    { location }
                                                </LabelContainer>

                                                <LabelContainer label="Booking Date & Duration">
                                                    { getDSTLocalTime(this.state.booking?.start).format('hh:mm A') } - { getDSTLocalTime(this.state?.booking?.end_less_post_booking_buffer_duration).format('hh:mm A - DD/MM/YYYY') }
                                                </LabelContainer>

                                                <List gap="20px">
                                                    <LabelContainer label="Number of attendees">
                                                        { this.state.booking?.no_attendees }
                                                    </LabelContainer>
                                                    { this.state.booking.canceled === null && <GuestListButton booking={this.state.booking} onCreate={this.fetchBooking.bind(this)}/> }
                                                </List>
                                            </List>
                                        </Container.Content>
                                    </Container>

                                    <Addons
                                        addons={this.state.addons}
                                        packages={this.state.booking?.add_on_package_bookings}
                                        booking={this.state.booking}
                                        onAdditionalDetailsAdd={this.fetchBooking.bind(this)}
                                    />

                                    <BillingDetails
                                        viewOnly
                                        booker={quote?.billing_profile}
                                    />
                                </List>

                                <Container className="booking-view__child">
                                    <Container.Header>
                                        <Container.Text color="#FF6933" fontSize="18px">Your Order</Container.Text>
                                    </Container.Header>

                                    <Container.Content>
                                        <List gap="40px">
                                            <div className="agree-box">
                                                <Summary
                                                    addons={this.state.addons}
                                                    addonsValues={this.state.params?.addons}
                                                    quote={this.state.booking?.saved_quote}
                                                />
                                                <div className="agree-box__content">
                                                    <div className="agree-box__details">
                                                        <div className="agree-box-details__detail">
                                                            <p>Credits Used</p>
                                                            <span>{ this.state.booking?.saved_quote?.credits_used }</span>
                                                        </div>
                                                        <div className="agree-box-details__detail">
                                                            <p>BACS Due</p>
                                                            <span>£{ this.state.booking?.saved_quote?.total }</span>
                                                        </div>
                                                    </div>

                                                    { this.state.booking?.receipt && <ButtonDownload color="black">Download Reciept</ButtonDownload> }
                                                </div>
                                            </div>

                                            <Container>
                                                <Container.Header>
                                                    <Container.Text fontSize="18px" color="#FF6933">Booking Information</Container.Text>
                                                </Container.Header>

                                                <Container.Content>
                                                    <Container.Text fontSize="14px" lightThemeColor="#24191F" darkThemeColor="white" html={this.state.booking?.saved_quote?.meta?.meeting_room?.booking_information}></Container.Text>
                                                </Container.Content>
                                            </Container>

                                            <Container>
                                                <Container.Header>
                                                    <Container.Text fontSize="18px" color="#FF6933">Contact Us</Container.Text>
                                                </Container.Header>

                                                <Container.Content>
                                                    <Container.Text fontSize="14px" lightThemeColor="#24191F" darkThemeColor="white">
                                                        +44 (0)1226 720 700

                                                        bookings@barnsleydmc.co.uk

                                                        DMC
                                                        County Way
                                                        Barnsley
                                                        S70 2JW
                                                        UK
                                                    </Container.Text>
                                                </Container.Content>
                                            </Container>
                                        </List>
                                    </Container.Content>
                                </Container>
                            </div>
                        </List>
                    }
                />
            </Layout>    
        )
    }
}

const el = document.getElementById('booking-completion');

if (el) {
    ReactDOM.render(<BookingsView id={el.getAttribute('data-id')}/>, el);
}
